import {
  getHalfHourlyIncrements,
  HalfHourlyData,
} from "@givenergy/sales-simple-energy-comparison";
import { Optional, OptionalHalfHourlyTariff, TariffPoint } from "@lib/types.ts";

// The maths of how the pricing calculation uses "half-hour starting", and is not inclusive of the "to" time period.
// Mixing what conceptually makes sense to the user and works for the data source without overlapping is awkward in this case.
// This works around the exclusivity behaviours built into the UI, so that it makes more sense for the user.
// If a user selected "02:00 -> 05:00", then the actual value would be "02:00 -> 04:30" as "to" is exclusive.
// This is then mapped out not including the final "to" value as the underlying data structure prefers.
export function buildHalfHourlyTariffFromPoints(tariffPoints: TariffPoint[]) {
  const increments = getHalfHourlyIncrements();
  const fullData: OptionalHalfHourlyTariff = {
    type: "tariff",
    ...(Object.fromEntries(
      increments.map((halfHourlyIncrement) => [halfHourlyIncrement, undefined]),
    ) as Optional<HalfHourlyData>),
  };

  // The tariff points are sanitised when added to the list, so they should not overlap, but do work
  // so that "to" is exclusive.
  tariffPoints.forEach((tariff) => {
    const fromIndex = increments.indexOf(tariff.from);
    const toIndex = increments.indexOf(tariff.to);

    if (fromIndex === -1 || toIndex === -1) {
      throw new Error(
        `Invalid indexes for from ${tariff.from} at ${fromIndex} or to ${tariff.to} at ${toIndex}`,
      );
    }

    const incrementToIndex =
      // If we're doing something like 00:00 -> 00:00 for a full day, then we just want the full list
      toIndex === fromIndex
        ? toIndex + increments.length + 1 // +1 to include ourselves as .slice is exclusive
        : // Otherwise, if our toIndex is before our fromIndex, then we are looping
          // we're not using +1 here as the exclusive behaviour better aligns with what "to" is for users conceptually
          toIndex < fromIndex
          ? toIndex + increments.length
          : toIndex;

    const incrementsToWrite = [...increments, ...increments].slice(
      fromIndex,
      incrementToIndex,
    );

    const updateObject = Object.fromEntries(
      incrementsToWrite.map((increment) => [increment, tariff.price]),
    );

    Object.assign(fullData, updateObject);
  });

  return fullData;
}
